<div class="sds-scanner">
  <!-- ═══════════ LOADING ═══════════ -->
  <div class="sds-scanner-state" *ngIf="loading">
    <i class="pi pi-spin pi-spinner"></i>
    <span [translate]="'SDS_PUBLIC_LIST_LOADING'"></span>
  </div>

  <!-- ═══════════ LINK NO LONGER VALID (AC5) ═══════════
       A dead code is a dead end, so this is a page rather than an error line: the same header
       and the same emergency footer as the list, because whoever scanned is still standing
       next to a chemical and still needs the numbers. -->
  <ng-container *ngIf="!loading && loadError === 'unavailable'">
    <ng-container *ngTemplateOutlet="guestHeader"></ng-container>

    <main class="sds-scanner-notice">
      <div class="sds-scanner-notice-card">
        <img
          src="assets/images/revoked-image.png"
          alt=""
          aria-hidden="true"
          class="sds-scanner-notice-image"
        />
        <h1 [translate]="'SDS_PUBLIC_LIST_UNAVAILABLE_TITLE'"></h1>
        <p [translate]="'SDS_PUBLIC_LIST_UNAVAILABLE_MESSAGE'"></p>
        <div class="sds-scanner-notice-hint">
          <i class="pi pi-info-circle"></i>
          <span [translate]="'SDS_PUBLIC_LIST_UNAVAILABLE_HINT'"></span>
        </div>
      </div>

      <img src="assets/images/KubaLogo.png" alt="Kuba" class="sds-scanner-notice-logo" />
    </main>

    <ng-container *ngTemplateOutlet="emergencyFooter"></ng-container>
  </ng-container>

  <!-- ═══════════ TOO MANY REQUESTS ═══════════
       The API's per-client budget is spent. Neither "no longer available" nor "check your
       connection" is true, and both would send the guest looking for a problem that is not
       theirs: a whole site behind one address shares that budget. -->
  <ng-container *ngIf="!loading && loadError === 'throttled'">
    <ng-container *ngTemplateOutlet="guestHeader"></ng-container>

    <main class="sds-scanner-notice">
      <div class="sds-scanner-notice-card">
        <span class="sds-scanner-notice-icon"><i class="pi pi-hourglass"></i></span>
        <h1 [translate]="'SDS_PUBLIC_LIST_THROTTLED_TITLE'"></h1>
        <p [translate]="'SDS_PUBLIC_LIST_THROTTLED_MESSAGE'"></p>
        <!-- Only when the API said how long; a made-up number would be worse than none. -->
        <p class="sds-scanner-notice-wait" *ngIf="retryAfterSeconds">
          {{ 'SDS_PUBLIC_LIST_THROTTLED_WAIT' | translate: { seconds: retryAfterSeconds } }}
        </p>
        <button type="button" class="sds-scanner-btn sds-scanner-btn--accent" (click)="retry()">
          <i class="pi pi-refresh"></i>
          <span [translate]="'SDS_PUBLIC_LIST_RETRY'"></span>
        </button>
      </div>

      <img src="assets/images/KubaLogo.png" alt="Kuba" class="sds-scanner-notice-logo" />
    </main>

    <ng-container *ngTemplateOutlet="emergencyFooter"></ng-container>
  </ng-container>

  <!-- ═══════════ COULD NOT REACH THE SERVER ═══════════ -->
  <ng-container *ngIf="!loading && loadError === 'network'">
    <ng-container *ngTemplateOutlet="guestHeader"></ng-container>

    <main class="sds-scanner-notice">
      <div class="sds-scanner-notice-card">
        <span class="sds-scanner-notice-icon"><i class="pi pi-wifi"></i></span>
        <h1 [translate]="'SDS_PUBLIC_LIST_NETWORK_TITLE'"></h1>
        <p [translate]="'SDS_PUBLIC_LIST_NETWORK_MESSAGE'"></p>
        <button type="button" class="sds-scanner-btn sds-scanner-btn--accent" (click)="retry()">
          <i class="pi pi-refresh"></i>
          <span [translate]="'SDS_PUBLIC_LIST_RETRY'"></span>
        </button>
      </div>

      <img src="assets/images/KubaLogo.png" alt="Kuba" class="sds-scanner-notice-logo" />
    </main>

    <ng-container *ngTemplateOutlet="emergencyFooter"></ng-container>
  </ng-container>

  <!-- ═══════════ THE LIST ═══════════ -->
  <ng-container *ngIf="!loading && !loadError && context">
    <!-- ─── Scrolling column: the header scrolls away, the search bar does not ─── -->
    <main class="sds-scanner-scroll" #scrollArea>
      <header class="sds-scanner-topbar">
        <div class="sds-scanner-brand">
          <img src="assets/images/KubaLogowhite.png" alt="Kuba" class="sds-scanner-brand-logo" />
          <span class="sds-scanner-brand-divider"></span>
          <span class="sds-scanner-brand-module" [translate]="'CHEMICALS'"></span>

          <!-- A guest has no profile to read a language from, so the page asks. -->
          <nav class="sds-scanner-lang" [attr.aria-label]="'LANGUAGE' | translate">
            <button
              type="button"
              [class.is-active]="currentLang === 'no'"
              [attr.aria-pressed]="currentLang === 'no'"
              (click)="setLanguage('no')"
              lang="no"
            >NO</button>
            <button
              type="button"
              [class.is-active]="currentLang === 'en'"
              [attr.aria-pressed]="currentLang === 'en'"
              (click)="setLanguage('en')"
              lang="en"
            >EN</button>
          </nav>
        </div>

        <div class="sds-scanner-identity">
          <img
            *ngIf="context.BusinessLogo"
            [src]="context.BusinessLogo"
            [alt]="context.BusinessName"
            class="sds-scanner-business-logo"
          />
          <h1 [attr.title]="context.BusinessName">{{ context.BusinessName }}</h1>
          <span class="sds-scanner-readonly">
            <i class="pi pi-lock"></i>
            <span [translate]="'SDS_PUBLIC_LIST_READ_ONLY'"></span>
          </span>
        </div>
      </header>

      <!-- ─── Search + filter trigger: sticks to the top of the column ─── -->
      <div class="sds-scanner-searchbar">
        <div class="sds-scanner-search">
          <i class="pi pi-search"></i>
          <input
            type="search"
            [value]="searchTerm"
            (input)="onSearch($any($event.target)?.value)"
            [placeholder]="'SDS_PUBLIC_LIST_SEARCH_PLACEHOLDER' | translate"
            [attr.aria-label]="'SDS_PUBLIC_LIST_SEARCH_PLACEHOLDER' | translate"
          />
        </div>
        <!-- Filters live in a bottom sheet: stacked down the page they cost half a phone
             screen before the first product is visible. -->
        <button
          type="button"
          class="sds-scanner-filter-trigger"
          [class.is-active]="hasActiveFilters"
          (click)="openFilterSheet()"
          [attr.aria-label]="'SDS_PUBLIC_LIST_FILTER_TITLE' | translate"
        >
          <i class="pi pi-sliders-h"></i>
          <span class="sds-scanner-filter-count" *ngIf="hasActiveFilters">{{ activeFilterCount }}</span>
        </button>
      </div>

      <div class="sds-scanner-body">
        <div class="sds-scanner-resultline">
          <span class="sds-scanner-count">{{ 'SDS_PUBLIC_LIST_COUNT' | translate: { count: totalCount } }}</span>
          <span class="sds-scanner-sortlabel">
            {{ (sortColumn === 'Manufacturer' ? 'SDS_SUPPLIER' : 'SDS_PRODUCT_NAME') | translate }}
            {{ sortDirection === 'ASC' ? 'A–Å' : 'Å–A' }}
          </span>
        </div>

        <div class="sds-scanner-chips" *ngIf="hasActiveFilters">
          <button
            type="button"
            class="sds-scanner-chip"
            *ngFor="let chip of activeFilterChips"
            (click)="chip.clear()"
          >
            {{ chip.label }}<i class="pi pi-times"></i>
          </button>
          <button type="button" class="sds-scanner-chip-reset" (click)="clearFilters()">
            <span [translate]="'CLEAR_FILTERS'"></span>
          </button>
        </div>

        <div class="sds-scanner-rows" [class.is-loading]="rowsLoading">
          <div class="sds-scanner-rows-overlay" *ngIf="rowsLoading">
            <i class="pi pi-spin pi-spinner"></i>
          </div>

          <article class="sds-scanner-card" *ngFor="let row of rows; trackBy: trackByRowId">
            <div class="sds-scanner-card-head">
              <div class="sds-scanner-card-names">
                <h2 [attr.title]="row.Product">{{ row.Product }}</h2>
                <p *ngIf="row.Manufacturer" [attr.title]="row.Manufacturer">{{ row.Manufacturer }}</p>
              </div>
              <span
                class="sds-scanner-risk"
                *ngIf="row.RiskLevel"
                [ngClass]="riskClass(row.RiskLevel)"
              >
                <i [class]="riskIcon(row.RiskLevel)"></i>{{ riskLabel(row.RiskLevel) }}
              </span>
            </div>

            <div class="sds-scanner-pictos">
              <img
                *ngFor="let pictogram of visiblePictograms(row)"
                [src]="pictogramSvg(pictogram)"
                [alt]="pictogramLabel(pictogram)"
                [pTooltip]="pictogramLabel(pictogram)"
                tooltipPosition="top"
              />
              <!-- Hidden hazard symbols stay reachable: the badge counts them, names them on
                   hover, and opens the detail sheet where all of them are listed. -->
              <button
                *ngIf="hiddenPictogramCount(row) as hiddenCount"
                type="button"
                class="sds-scanner-picto-more"
                (click)="openDetails(row)"
                [pTooltip]="hiddenPictogramNames(row)"
                tooltipPosition="top"
                [attr.aria-label]="hiddenPictogramNames(row)"
              >+{{ hiddenCount }}</button>

              <span class="sds-scanner-noclass" *ngIf="!row.Pictograms.length">
                <i class="pi pi-check-circle"></i>
                <span [translate]="'SDS_PUBLIC_LIST_NO_CLASSIFICATION'"></span>
              </span>
            </div>

            <div class="sds-scanner-codes" *ngIf="row.HazardStatementCodes.length">
              {{ row.HazardStatementCodes.join(' · ') }}
            </div>

            <div class="sds-scanner-card-actions">
              <!-- The safety data sheet is what people came for, so it gets the primary button. -->
              <button
                type="button"
                class="sds-scanner-btn sds-scanner-btn--primary"
                *ngIf="row.HasSafetyDataSheet"
                (click)="openSafetyDataSheet(row)"
              >
                <i class="pi pi-file-pdf"></i>
                <span [translate]="'SDS_PUBLIC_LIST_OPEN_SDS'"></span>
              </button>
              <span class="sds-scanner-btn sds-scanner-btn--disabled" *ngIf="!row.HasSafetyDataSheet">
                <i class="pi pi-file-excel"></i>
                <span [translate]="'SDS_NO_PDF_UPLOADED'"></span>
              </span>

              <button
                type="button"
                class="sds-scanner-iconbtn"
                (click)="openDetails(row)"
                [attr.aria-label]="'SDS_PUBLIC_LIST_DETAILS' | translate"
                [pTooltip]="'SDS_PUBLIC_LIST_DETAILS' | translate"
                tooltipPosition="top"
              >
                <i class="pi pi-info-circle"></i>
              </button>
            </div>
          </article>

          <!-- Empty state -->
          <div class="sds-scanner-empty" *ngIf="!rowsLoading && !rows.length">
            <span class="sds-scanner-empty-icon"><i class="pi pi-search-minus"></i></span>
            <h2 [translate]="'NO_RECORDS_FOUND'"></h2>
            <p [translate]="'SDS_PUBLIC_LIST_EMPTY_HINT'"></p>
            <button type="button" class="sds-scanner-btn sds-scanner-btn--accent" (click)="clearEverything()">
              <span [translate]="'SDS_PUBLIC_LIST_SHOW_ALL'"></span>
            </button>
          </div>
        </div>

        <!-- The list grows as the guest scrolls. The button is the fallback: it is what a
             failed fetch leaves behind, and what a keyboard or a screen reader can reach. -->
        <div class="sds-scanner-more" *ngIf="!rowsLoading && (hasMorePages || appendingPage)">
          <i class="pi pi-spin pi-spinner sds-scanner-more-spinner" *ngIf="appendingPage"></i>

          <span class="sds-scanner-more-error" *ngIf="appendError">
            <ng-container *ngIf="appendError === 'throttled'; else appendNetworkError">
              {{ retryAfterSeconds
                  ? ('SDS_PUBLIC_LIST_THROTTLED_WAIT' | translate: { seconds: retryAfterSeconds })
                  : ('SDS_PUBLIC_LIST_THROTTLED_MESSAGE' | translate) }}
            </ng-container>
            <ng-template #appendNetworkError>
              <span [translate]="'SDS_PUBLIC_LIST_NETWORK_MESSAGE'"></span>
            </ng-template>
          </span>

          <button type="button" *ngIf="!appendingPage" (click)="loadMore()">
            <i class="pi" [ngClass]="appendError ? 'pi-refresh' : 'pi-chevron-down'"></i>
            <span [translate]="appendError ? 'SDS_PUBLIC_LIST_RETRY' : 'SDS_PUBLIC_LIST_SHOW_MORE'"></span>
          </button>

          <span>{{ 'SDS_PUBLIC_LIST_LOADED' | translate: { loaded: rows.length, total: totalCount } }}</span>
        </div>
      </div>
    </main>

    <ng-container *ngTemplateOutlet="emergencyFooter"></ng-container>

    <!-- ─── Filter bottom sheet ─── -->
    <div class="sds-scanner-scrim" *ngIf="filterSheetOpen" (click)="closeFilterSheet()"></div>
    <section
      class="sds-scanner-sheet"
      *ngIf="filterSheetOpen"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="'SDS_PUBLIC_LIST_FILTER_TITLE' | translate"
    >
      <div class="sds-scanner-sheet-grip"><span></span></div>
      <div class="sds-scanner-sheet-head">
        <h2 [translate]="'SDS_PUBLIC_LIST_FILTER_TITLE'"></h2>
        <button type="button" class="sds-scanner-sheet-reset" (click)="clearFilters()">
          <span [translate]="'CLEAR_FILTERS'"></span>
        </button>
      </div>

      <div class="sds-scanner-sheet-body">
        <h3 [translate]="'SDS_PICTOGRAM'"></h3>
        <div class="sds-scanner-picto-grid">
          <button
            type="button"
            *ngFor="let choice of pictogramChoices"
            [class.is-selected]="pictogramFilter === choice.value"
            (click)="togglePictogram(choice.value)"
          >
            <img [src]="choice.svg" alt="" aria-hidden="true" />
            <span>{{ choice.label }}</span>
          </button>
        </div>

        <h3 [translate]="'RISK_FIGURE'"></h3>
        <div class="sds-scanner-pill-row">
          <button
            type="button"
            class="sds-scanner-risk-choice"
            *ngFor="let choice of riskChoices"
            [ngClass]="riskClass(choice.value)"
            [class.is-selected]="riskFilter === choice.value"
            (click)="toggleRisk(choice.value)"
          ><span class="sds-scanner-risk-dot"></span>{{ choice.label }}</button>
        </div>

        <h3 [translate]="'SDS_SUPPLIER'"></h3>
        <div class="sds-scanner-pill-row">
          <button
            type="button"
            *ngFor="let choice of manufacturerChoices"
            [class.is-selected]="manufacturerFilter === choice.value"
            (click)="toggleManufacturer(choice.value)"
          >{{ choice.label }}</button>
        </div>

        <h3 [translate]="'SORT_BY'"></h3>
        <div class="sds-scanner-segmented">
          <button
            type="button"
            [class.is-selected]="isSortActive('ProductName', 'ASC')"
            (click)="pickSort('ProductName', 'ASC')"
          >A–Å</button>
          <button
            type="button"
            [class.is-selected]="isSortActive('ProductName', 'DESC')"
            (click)="pickSort('ProductName', 'DESC')"
          >Å–A</button>
          <button
            type="button"
            [class.is-selected]="isSortActive('Manufacturer', 'ASC')"
            (click)="pickSort('Manufacturer', 'ASC')"
            [translate]="'SDS_SUPPLIER'"
          ></button>
        </div>
      </div>

      <div class="sds-scanner-sheet-foot">
        <button type="button" class="sds-scanner-btn sds-scanner-btn--accent" (click)="closeFilterSheet()">
          {{ 'SDS_PUBLIC_LIST_SHOW_RESULTS' | translate: { count: totalCount } }}
        </button>
      </div>
    </section>

    <!-- ─── Detail sheet ─── -->
    <section
      class="sds-scanner-details"
      *ngIf="detailsRow as row"
      role="dialog"
      aria-modal="true"
      [attr.aria-label]="row.Product"
    >
      <header class="sds-scanner-details-head">
        <div>
          <span class="sds-scanner-details-eyebrow">
            <i class="pi pi-shield"></i>
            <span [translate]="'SDS_PUBLIC_LIST_DETAILS'"></span>
          </span>
          <h2>{{ row.Product }}</h2>
          <div class="sds-scanner-details-meta" *ngIf="row.Manufacturer">{{ row.Manufacturer }}</div>
        </div>
        <button
          type="button"
          class="sds-scanner-details-close"
          (click)="closeDetails()"
          [attr.aria-label]="'CLOSE' | translate"
        >
          <i class="pi pi-times"></i>
        </button>
      </header>

      <div class="sds-scanner-details-body">
        <!-- Every pictogram, named. This is where the "+N" badge leads. -->
        <div class="sds-scanner-details-pictos" *ngIf="row.Pictograms.length">
          <span *ngFor="let pictogram of row.Pictograms">
            <img [src]="pictogramSvg(pictogram)" alt="" aria-hidden="true" />
            <span>{{ pictogramLabel(pictogram) }}</span>
          </span>
        </div>

        <dl class="sds-scanner-details-list">
          <dt [translate]="'SDS_CAS_NUMBER'"></dt>
          <dd>{{ row.CASNumber || '—' }}</dd>

          <dt [translate]="'SDS_SUPPLIER'"></dt>
          <dd>{{ row.Manufacturer || '—' }}</dd>

          <dt [translate]="'RISK_FIGURE'"></dt>
          <dd>
            <span class="sds-scanner-risk" *ngIf="row.RiskLevel" [ngClass]="riskClass(row.RiskLevel)">
              <i [class]="riskIcon(row.RiskLevel)"></i>{{ riskLabel(row.RiskLevel) }}
            </span>
            <span *ngIf="row.RiskFigure"> · {{ row.RiskFigure }}</span>
            <span *ngIf="!row.RiskLevel && !row.RiskFigure">—</span>
          </dd>

          <dt [translate]="'SDS_H_STATEMENT_CODE'"></dt>
          <dd>
            <span class="sds-scanner-hcode" *ngFor="let code of row.HazardStatementCodes">{{ code }}</span>
            <span *ngIf="!row.HazardStatementCodes.length">—</span>
          </dd>
        </dl>
      </div>

      <div class="sds-scanner-details-foot">
        <button
          type="button"
          class="sds-scanner-btn sds-scanner-btn--primary"
          *ngIf="row.HasSafetyDataSheet"
          (click)="openSafetyDataSheet(row)"
        >
          <i class="pi pi-file-pdf"></i>
          <span [translate]="'SDS_PUBLIC_LIST_OPEN_SDS'"></span>
        </button>
        <p class="sds-scanner-nosds" *ngIf="!row.HasSafetyDataSheet" [translate]="'SDS_NO_PDF_UPLOADED'"></p>
      </div>
    </section>
  </ng-container>

  <!-- ─── Header for the pages that never reached a list ───
       Brand and language only: there is no business to name, and the toggle has to be here
       because it is the last place left to change the language of the message they did get. -->
  <ng-template #guestHeader>
    <header class="sds-scanner-topbar">
      <div class="sds-scanner-brand">
        <img src="assets/images/KubaLogowhite.png" alt="Kuba" class="sds-scanner-brand-logo" />
        <span class="sds-scanner-brand-divider"></span>
        <span class="sds-scanner-brand-module" [translate]="'CHEMICALS'"></span>

        <nav class="sds-scanner-lang" [attr.aria-label]="'LANGUAGE' | translate">
          <button
            type="button"
            [class.is-active]="currentLang === 'no'"
            [attr.aria-pressed]="currentLang === 'no'"
            (click)="setLanguage('no')"
            lang="no"
          >NO</button>
          <button
            type="button"
            [class.is-active]="currentLang === 'en'"
            [attr.aria-pressed]="currentLang === 'en'"
            (click)="setLanguage('en')"
            lang="en"
          >EN</button>
        </nav>
      </div>
    </header>
  </ng-template>

  <!-- ─── Sticky emergency footer ───
       One definition, used by the list and by the "no longer available" page: the numbers are
       the reason the footer exists, and they are no less needed when the code is dead. -->
  <ng-template #emergencyFooter>
    <footer class="sds-scanner-emergency">
      <div class="sds-scanner-emergency-label">
        <i class="pi pi-phone"></i>
        <span [translate]="'SDS_PUBLIC_LIST_EMERGENCY'"></span>
      </div>
      <div class="sds-scanner-emergency-links">
        <a [href]="'tel:' + poisonInfoPhone" class="sds-scanner-emergency-poison">
          <span [translate]="'SDS_PUBLIC_LIST_POISON_INFO'"></span>
          <strong>{{ poisonInfoDisplay }}</strong>
        </a>
        <a [href]="'tel:' + emergencyPhone" class="sds-scanner-emergency-ambulance">
          <span [translate]="'SDS_PUBLIC_LIST_AMBULANCE'"></span>
          <strong>{{ emergencyPhone }}</strong>
        </a>
      </div>
    </footer>
  </ng-template>
</div>
